"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import type { ExchangeCard, ResponseOption } from "@/lib/flatten-dialog";
import { replacePlaceholders, detectPlaceholders } from "@/lib/flatten-dialog";
import { SpeakerButton } from "../speaker-button";
import type { TTSState } from "@/lib/use-tts";

// Subtle pastel palette for word pills
const PILL_COLORS = [
  "bg-sky-100/70 dark:bg-sky-900/25",
  "bg-amber-100/70 dark:bg-amber-900/25",
  "bg-emerald-100/70 dark:bg-emerald-900/25",
  "bg-rose-100/70 dark:bg-rose-900/25",
  "bg-violet-100/70 dark:bg-violet-900/25",
  "bg-teal-100/70 dark:bg-teal-900/25",
];

/** Split text into phrase groups (by punctuation), keeping words together within each phrase */
function splitIntoPhrases(text: string): string[][] {
  // Split after punctuation followed by space
  const phrases = text.split(/(?<=[,.:;!?])\s+/).filter(Boolean);
  return (phrases.length > 0 ? phrases : [text]).map(p => p.split(/\s+/).filter(Boolean));
}

function PhraseChips({ text, className }: { text: string; className?: string }) {
  const phrases = splitIntoPhrases(text);
  const allWords = phrases.flat();
  if (allWords.length <= 1) return <span>{text}</span>;

  let wi = 0;
  return (
    <span className="flex flex-wrap gap-1.5 items-baseline">
      {phrases.map((words, pi) => (
        <span key={pi} className="flex flex-nowrap gap-1.5">
          {words.map((word) => {
            const idx = wi++;
            return (
              <span
                key={idx}
                className={`inline-block px-2 py-0.5 rounded-lg ${PILL_COLORS[idx % PILL_COLORS.length]} ${className || ""}`}
              >
                {word}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

/** Render text with vocalization tip, one pill per word.
 *  Words within the same phrase (between punctuation) stay on one line. */
function WordAlignedText({
  text,
  tip,
  wordClassName,
  tipClassName,
}: {
  text: string;
  tip: string;
  wordClassName?: string;
  tipClassName?: string;
}) {
  const phrases = splitIntoPhrases(text);
  const tipPhrases = splitIntoPhrases(tip);
  const allWords = phrases.flat();

  const allTipWords = tipPhrases.flat();
  let wi = 0;
  return (
    <span className="flex flex-wrap gap-1.5 items-start">
      {phrases.map((words, pi) => (
        <span key={pi} className="flex flex-nowrap gap-1.5">
          {words.map((word) => {
            const idx = wi++;
            const tipWord = allTipWords[idx] || "";
            const hasTip = tipWord && tipWord.toLowerCase() !== word.toLowerCase();
            const pillColor = allWords.length > 1 ? PILL_COLORS[idx % PILL_COLORS.length] : "";
            return (
              <span
                key={idx}
                className={`inline-flex flex-col rounded-lg overflow-hidden ${pillColor || "bg-muted/30"}`}
              >
                <span className={`px-2.5 pt-1.5 ${hasTip ? "pb-1" : "pb-1.5"} ${wordClassName || ""}`}>
                  {word}
                </span>
                {hasTip && (
                  <span className={`px-2.5 pb-1.5 pt-0.5 bg-black/[0.04] dark:bg-white/[0.06] ${tipClassName || ""}`}>
                    {tipWord}
                  </span>
                )}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

interface CueCardProps {
  card: ExchangeCard;
  userDetails: Record<string, string>;
  index: number;
  total: number;
  onSpeak: (text: string) => void;
  onStopSpeak: () => void;
  ttsState: TTSState;
}

export function CueCard({
  card,
  userDetails,
  index,
  total,
  onSpeak,
  onStopSpeak,
  ttsState,
}: CueCardProps) {
  const [activeResponse, setActiveResponse] = useState<ResponseOption | null>(
    card.response
  );

  useEffect(() => {
    setActiveResponse(card.response);
  }, [card.response]);

  const otherOptions = useMemo(() => {
    if (!card.response) return [];
    const allOptions = [
      {
        id: card.response.id,
        text: card.response.text,
        translation: card.response.translation,
        vocalizationTip: card.response.vocalizationTip,
      },
      ...card.alternatives,
    ];
    return allOptions.filter((o) => o.id !== activeResponse?.id);
  }, [card.response, card.alternatives, activeResponse?.id]);

  const handleAlternativeTap = useCallback(
    (alt: { id: string; text: string; translation: string; vocalizationTip?: string }) => {
      const placeholders = detectPlaceholders(alt.text);
      setActiveResponse({
        id: alt.id,
        text: alt.text,
        translation: alt.translation,
        vocalizationTip: alt.vocalizationTip,
        hasPlaceholder: placeholders.length > 0,
        placeholders,
      });
    },
    []
  );

  const handleSpeak = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!activeResponse) return;
      if (ttsState === "playing") {
        onStopSpeak();
      } else {
        const text = activeResponse.hasPlaceholder
          ? replacePlaceholders(activeResponse.text, userDetails)
          : activeResponse.text;
        onSpeak(text);
      }
    },
    [activeResponse, ttsState, onSpeak, onStopSpeak, userDetails]
  );

  const resolveText = (text: string, hasPlaceholder: boolean) =>
    hasPlaceholder ? replacePlaceholders(text, userDetails) : text;

  return (
    <div className="h-full rounded-2xl bg-card shadow-lg border border-border/50 p-5 flex flex-col select-none overflow-y-auto">
      {/* They say (context) */}
      <div className="mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/40">
          They say
        </span>
        <p className="text-xs text-muted-foreground/50 leading-snug mt-0.5">
          {card.prompt.text}
        </p>
        <p className="text-[10px] text-muted-foreground/30 mt-0.5">
          {card.prompt.translation}
        </p>
      </div>

      {/* You say (hero section) */}
      {activeResponse ? (
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              You say
            </span>
            <SpeakerButton
              state={ttsState}
              onClick={handleSpeak}
              size="sm"
            />
          </div>

          {/* Main text with word-aligned vocalization */}
          {activeResponse.vocalizationTip ? (
            <div className="leading-snug">
              <WordAlignedText
                text={resolveText(activeResponse.text, activeResponse.hasPlaceholder)}
                tip={resolveText(activeResponse.vocalizationTip, activeResponse.hasPlaceholder)}
                wordClassName="text-[22px] font-bold text-foreground"
                tipClassName="text-[14px] text-primary/70 italic leading-none"
              />
            </div>
          ) : (
            <div className="text-[22px] font-bold text-foreground leading-snug">
              <PhraseChips text={resolveText(activeResponse.text, activeResponse.hasPlaceholder)} />
            </div>
          )}

          {/* Translation */}
          <p className="text-sm text-muted-foreground/60 mt-2">
            {resolveText(activeResponse.translation, activeResponse.hasPlaceholder)}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground italic">End of conversation</p>
        </div>
      )}

      {/* Alternatives */}
      {otherOptions.length > 0 && (
        <div className="mt-3 pt-2 border-t border-border/20">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/40 mb-1.5 block">
            Also try
          </span>
          <div className="flex flex-wrap gap-1.5">
            {otherOptions.map((alt) => (
              <button
                key={alt.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAlternativeTap(alt);
                }}
                className="pressable text-xs px-2.5 py-1.5 rounded-full bg-muted/80 text-foreground/70 hover:bg-primary/10 hover:text-primary transition-colors text-left leading-tight max-w-[220px] truncate"
              >
                {alt.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Counter */}
      <div className="flex justify-end mt-2">
        <span className="text-[10px] text-muted-foreground/30 tabular-nums">
          {index + 1} / {total}
        </span>
      </div>
    </div>
  );
}
